export type TicketDetail = {
  id?: string;
  ticket_id?: string;
  user_id?: string;
  userId?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  user?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  };
  [key: string]: unknown;
};

export type ScanTicketResponse = {
  message?: string;
  validate?: boolean;
  isExpired?: boolean;
  isExtendable?: boolean;
  canExtend?: boolean;
  isPastTicket?: boolean;
  isVisitToday?: boolean;
  requiresExtension?: boolean;
  ticketStatus?: string;
  ticketExpiresAt?: string;
  ticketStartTime?: string;
  expiresInMilliseconds?: number;
  durationSeconds?: number;
  extensionCount?: number;
  serverTime?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  ticket?: {
    id: string;
    ticket_id?: string;
    first_name?: string;
    last_name?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    ticket_status?: string;
    user_id?: string;
    user?: {
      first_name?: string;
      last_name?: string;
      firstName?: string;
      lastName?: string;
      name?: string;
    };
    [key: string]: unknown;
  };
};

export type TicketExtensionEvent = {
  ticketId?: string;
  ticket_id?: string;
  ticketID?: string;
  id?: string;
  ticket?: {
    id?: string;
    ticketId?: string;
    ticket_id?: string;
    ticketID?: string;
  };
  data?: {
    ticketId?: string;
    ticket_id?: string;
    ticketID?: string;
    id?: string;
    ticket?: {
      id?: string;
      ticketId?: string;
      ticket_id?: string;
      ticketID?: string;
    };
  };
};

export const TICKET_DETAIL_STORAGE_KEY = "ticket_detail";

export const DEFAULT_SUCCESS_MESSAGE =
  "QR Code Ticket Successfully verified!";
export const TICKET_EXTENDED_MESSAGE = "Ticket extended successfully!";
export const DEFAULT_EXPIRED_TICKET_MESSAGE =
  "This ticket has expired. Please purchase a new ticket.";
export const DEFAULT_INVALID_MESSAGE =
  "Invalid ticket. Check ticket date & time.";
export const CAMERA_ACCESS_DENIED_MESSAGE = "Camera access denied.";

const asTrimmedString = (value: unknown): string => {
  if (value == null) return "";
  return String(value).trim();
};

const readNonEmptyField = (
  obj: Record<string, unknown> | undefined,
  ...keys: string[]
): string => {
  if (!obj) return "";
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

const pickNameField = (
  source: Record<string, unknown> | undefined,
  snake: "first_name" | "last_name",
  camel: "firstName" | "lastName"
): string => {
  if (!source) return "";
  return asTrimmedString(source[snake]) || asTrimmedString(source[camel]);
};

export const getTicketNameFields = (
  ticket: Record<string, unknown> | null | undefined
): { first_name: string; last_name: string } => {
  if (!ticket) return { first_name: "", last_name: "" };

  let first_name = pickNameField(ticket, "first_name", "firstName");
  let last_name = pickNameField(ticket, "last_name", "lastName");

  const user =
    ticket.user && typeof ticket.user === "object" && !Array.isArray(ticket.user)
      ? (ticket.user as Record<string, unknown>)
      : undefined;
  if (!first_name) first_name = pickNameField(user, "first_name", "firstName");
  if (!last_name) last_name = pickNameField(user, "last_name", "lastName");

  const fullName =
    asTrimmedString(ticket.name) ||
    asTrimmedString(ticket.full_name) ||
    asTrimmedString(ticket.fullName) ||
    asTrimmedString(ticket.holder_name);
  if (!first_name && fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    first_name = parts[0] || "";
    if (!last_name && parts.length > 1) {
      last_name = parts.slice(1).join(" ");
    }
  }

  return { first_name, last_name };
};

export const hasTicketUserName = (
  ticket: Record<string, unknown> | null | undefined
): boolean => {
  const { first_name, last_name } = getTicketNameFields(ticket);
  return first_name.length > 0 && last_name.length > 0;
};

export const getTicketDisplayName = (
  ticket: Record<string, unknown> | null | undefined
): string => {
  const { first_name, last_name } = getTicketNameFields(ticket);
  return [first_name, last_name].filter(Boolean).join(" ").trim();
};

export const normalizeTicketDetail = (
  raw: TicketDetail | ScanTicketResponse | Record<string, unknown> | null
): TicketDetail | null => {
  if (!raw || typeof raw !== "object") return null;

  const root = raw as Record<string, unknown>;
  const nestedTicket =
    root.ticket && typeof root.ticket === "object"
      ? (root.ticket as Record<string, unknown>)
      : root;
  const user =
    nestedTicket.user && typeof nestedTicket.user === "object"
      ? (nestedTicket.user as Record<string, unknown>)
      : null;

  let first_name =
    readNonEmptyField(nestedTicket, "first_name", "firstName") ||
    (user ? readNonEmptyField(user, "first_name", "firstName") : "");
  let last_name =
    readNonEmptyField(nestedTicket, "last_name", "lastName") ||
    (user ? readNonEmptyField(user, "last_name", "lastName") : "");

  if (!first_name || !last_name) {
    const fullName =
      readNonEmptyField(
        nestedTicket,
        "name",
        "full_name",
        "fullName",
        "holder_name",
        "userName"
      ) ||
      (user
        ? readNonEmptyField(user, "name", "full_name", "fullName")
        : "");
    if (fullName) {
      const parts = fullName.split(/\s+/).filter(Boolean);
      if (!first_name) first_name = parts[0] ?? "";
      if (!last_name) {
        last_name = parts.length > 1 ? parts.slice(1).join(" ") : parts[0] ?? "";
      }
    }
  }

  const id =
    readNonEmptyField(nestedTicket, "id", "ticketId", "ticket_id") ||
    readNonEmptyField(root, "id", "ticketId", "ticket_id");
  const user_id =
    readNonEmptyField(nestedTicket, "user_id", "userId") ||
    (user ? readNonEmptyField(user, "id", "user_id", "userId") : "") ||
    readNonEmptyField(root, "user_id", "userId");

  return {
    ...(nestedTicket as TicketDetail),
    ...(id ? { id } : {}),
    ...(user_id ? { user_id } : {}),
    ...(first_name ? { first_name } : {}),
    ...(last_name ? { last_name } : {}),
  };
};

export const buildTicketDetailFromScanResponse = (
  data: ScanTicketResponse
): TicketDetail | null => normalizeTicketDetail(data);

export const getTicketDetailFromStorage = (): TicketDetail | null => {
  try {
    const stored = localStorage.getItem(TICKET_DETAIL_STORAGE_KEY);
    if (!stored) return null;
    return normalizeTicketDetail(JSON.parse(stored) as TicketDetail);
  } catch {
    return null;
  }
};

export const setTicketDetailInStorage = (ticket: TicketDetail): void => {
  const normalized = normalizeTicketDetail(ticket);
  if (normalized) {
    localStorage.setItem(TICKET_DETAIL_STORAGE_KEY, JSON.stringify(normalized));
  }
};

export const clearTicketDetailFromStorage = (): void => {
  localStorage.removeItem(TICKET_DETAIL_STORAGE_KEY);
};

export const getUserIdFromTicket = (
  ticket: TicketDetail | null
): string | null => {
  if (!ticket) return null;
  const id = ticket.user_id || ticket.userId || ticket.user?.id;
  return id ? String(id) : null;
};

export const getPaymentSubscriptionUserId = (): string | null => {
  const fromTicket = getUserIdFromTicket(getTicketDetailFromStorage());
  if (fromTicket) return fromTicket;
  return null;
};

export const getTicketIdFromScan = (decodedText: string): string => {
  const value = decodedText.trim();
  try {
    const scannedUrl = new URL(value);
    return scannedUrl.searchParams.get("ticketId") || value;
  } catch {
    return value;
  }
};

export const getExtendTicketUrl = (ticketId: string): string => {
  const webUrl = process.env.REACT_APP_FRONTEND_BASE_URL?.trim();
  if (!webUrl) {
    console.error("REACT_APP_FRONTEND_BASE_URL is not set.");
    return "";
  }
  try {
    const extendUrl = new URL("/extend-ticket", webUrl);
    extendUrl.searchParams.set("ticketId", ticketId);
    return extendUrl.toString();
  } catch {
    return "";
  }
};

export const isTicketExtendable = (data: {
  isExtendable?: boolean;
  canExtend?: boolean;
}) => data.isExtendable === true || data.canExtend === true;

export const normalizeId = (value: unknown) =>
  value == null ? "" : String(value).trim();

export const getTicketIdFromExtensionEvent = (
  payload: TicketExtensionEvent = {}
) =>
  normalizeId(
    payload.ticketId ||
      payload.ticket_id ||
      payload.ticketID ||
      payload.ticket?.id ||
      payload.ticket?.ticketId ||
      payload.ticket?.ticket_id ||
      payload.ticket?.ticketID ||
      payload.data?.ticketId ||
      payload.data?.ticket_id ||
      payload.data?.ticketID ||
      payload.data?.ticket?.id ||
      payload.data?.ticket?.ticketId ||
      payload.data?.ticket?.ticket_id ||
      payload.data?.ticket?.ticketID ||
      payload.id ||
      payload.data?.id
  );

export const isObjectExtensionPayload = (
  value: unknown
): value is TicketExtensionEvent =>
  typeof value === "object" && value !== null;
